import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ConnectionPointDto,
  GridConnectionLevel,
  MeterFrameDto,
  MeterFrameState,
  MeterFrameService,
  MeterFrameUnitType
} from '@root/app/api/nts';
import { InputGroupComponent } from '@root/app/shared/components/input/input-group/input-group.component';
import { InputDirective } from '@root/app/shared/components/input/input/input.directive';
import { LabelDirective } from '@root/app/shared/components/input/label/label.directive';
import { SelectDirective } from '@root/app/shared/components/input/select/select.directive';
import { FormApiService } from '@app/core/services/form-api.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { InputNumberDirective } from '@shared/components/input/number/input-number.directive';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { sortByTranslation } from '@app/core/utils/sort-by-translation';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

@Component({
  selector: 'app-meterframe-openform',
  imports: [
    InputDirective,
    InputGroupComponent,
    LabelDirective,
    ReactiveFormsModule,
    SelectDirective,
    TranslatePipe,
    InputNumberDirective,
  ],
  templateUrl: './meterframe-openform.component.html',
  styleUrl: './meterframe-openform.component.css',
})
export class MeterframeOpenformComponent implements OnInit, HasUnsavedChanges {
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public meterFrame?: MeterFrameDto;
  public availableMeterFrames?: MeterFrameDto[] = [];
  public meterFrames?: MeterFrameDto[] = [];
  public meterFramesList: KeyValue[] = [];
  public primaryMeterFrame?: MeterFrameDto

  public connectionPoint?: ConnectionPointDto;

  public gridConnectionLevelList: KeyValue[] = sortByTranslation(enumToKeyValue(GridConnectionLevel, { omitKeys: ['NotSpecified'], prefixValue: 'meterFrame.networkPlacementsOptions.' }));

  public unitTypeList: KeyValue[] = sortByTranslation(enumToKeyValue(MeterFrameUnitType, { omitKeys: ['NotSpecified'], prefixValue: 'meterFrame.unitTypeOptions.' }));

  public form: FormGroup = new FormGroup({
    id: new FormControl(null),
    meterFrameType: new FormControl(null, Validators.required),
    primaryMeterFrameId: new FormControl(null),
    connectionPointId: new FormControl(null),
    connectionPointIdentification: new FormControl(null),
    unitType: new FormControl(null),
    priority: new FormControl(1),
    identification: new FormControl(null, Validators.required),
    transformer: new FormControl(null),
    maximumPower: new FormControl(null),
    limitedGridAccess: new FormControl(null),
    maximumCurrent: new FormControl(null),
    networkPlacement: new FormControl(GridConnectionLevel.C),
    description: new FormControl(null),
    capacity: new FormControl(null),
    status: new FormControl(null),
    conversionFactorA14: new FormControl(1, Validators.required),
    conversionFactorA23: new FormControl(1, Validators.required),
    conversionFactorR12: new FormControl(1, Validators.required),
    conversionFactorR34: new FormControl(1, Validators.required),
  });

  constructor(
    private readonly meterFrameService: MeterFrameService,
    private readonly formApiService: FormApiService,
    private readonly translateService: TranslateService,
  ) {
  }

  public ngOnInit(): void {
    if (this.connectionPoint) {
      this.form.get('connectionPointId')?.setValue(this.connectionPoint.id);
      this.form.get('connectionPointIdentification')?.setValue(this.connectionPoint.identification);
    }

    this.form.get('meterFrameType')?.valueChanges.subscribe((type: string) => {
      this.updateValidators(type);

      if (!this.meterFrame && type) {
        const name = `meterFrame.types.${type.toLowerCase()}`;
        this.form.get('identification')?.setValue(
          this.translateService.instant(name)
        );
      }
    });

    this.form.get('unitType')?.valueChanges.subscribe((unit: MeterFrameUnitType | null) => {
      if (this.meterFrame) {
        return;
      }

      const defaults: Record<string, number> = {
        ChargingStation: 100,
        HeatPump: 200,
        BatterySystem: 220,
      };

      const value = unit ? defaults[String(unit)] : undefined;
      if (value !== undefined) {
        this.form.get('priority')?.setValue(value);
      }

      if (unit) {
        const unitTranslationKey = `meterFrame.unitTypeOptions.${String(unit).toLowerCase()}`;
        this.form.get('identification')?.setValue(this.translateService.instant(unitTranslationKey));
      }
    });

    if (this.availableMeterFrames) {
      for (const element of this.availableMeterFrames) {
        if (element.meterFrameType === 'Primary' || element.meterFrameType === 'NotSpecified') {
          if (this.meterFrames) {
            this.meterFrames.push(element);
          }
          if (this.meterFrame?.primaryMeterFrameId === element.id) {
            this.primaryMeterFrame = element;
          }
        }
      }

      this.meterFramesList = this.meterFrames?.map(el => ({ value: el.identification, key: el.id })) as KeyValue[];

      this.populateFormWithMeterFrame();
    }
  }

  public saveChanges(): void {
    this.formApiService.submit(
      this.form,
      (data) => this.meterFrameService.meterFrameEditPost(data),
      {
        successMessage: 'meterFrame.meterFrameEdited',
        closeModal: true,
      }
    );
  }

  public createMeterFrame(): void {
    // Stringify
    this.convertToString('maximumPower');
    this.convertToString('transformer');
    this.convertToString('maximumCurrent');
    this.convertToString('limitedGridAccess');

    this.form.get('status')?.setValue(MeterFrameState.Active);

    this.formApiService.submit(
      this.form,
      (data) => this.meterFrameService.meterFrameNewPost(data),
      {
        successMessage: 'meterFrame.meterFrameCreated',
        closeModal: true,
      }
    );
  }

  public convertToString(key: string): void {
    if (this.form.get(key)?.value) {
      this.form.get(key)?.setValue(String(this.form.get(key)?.value));
    }
  }

  private updateValidators(type: string | null): void {
    this.form.get('priority')?.clearValidators();
    this.form.get('primaryMeterFrameId')?.clearValidators();
    this.form.get('unitType')?.clearValidators();
    this.form.get('capacity')?.clearValidators();
    this.form.get('networkPlacement')?.clearValidators();

    if (type === 'Primary' || type === 'Station') {
      this.form.get('priority')?.setValidators(Validators.required);
      this.form.get('networkPlacement')?.setValidators(Validators.required);
    } else if (type === 'Serial' || type === 'Parallel') {
      this.form.get('primaryMeterFrameId')?.setValidators(Validators.required);
      this.form.get('unitType')?.setValidators(Validators.required);
      this.form.get('capacity')?.setValidators(Validators.required);
    }

    this.form.get('priority')?.updateValueAndValidity();
    this.form.get('primaryMeterFrameId')?.updateValueAndValidity();
    this.form.get('unitType')?.updateValueAndValidity();
    this.form.get('capacity')?.updateValueAndValidity();
    this.form.get('networkPlacement')?.updateValueAndValidity();
  }

  private populateFormWithMeterFrame(): void {
    if (this.meterFrame) {
      this.form.patchValue({
        id: this.meterFrame.id,
        meterFrameType: this.meterFrame.meterFrameType,
        primaryMeterFrameId: this.meterFrame.primaryMeterFrameId,
        unitType: this.meterFrame.unitType,
        priority: this.meterFrame.priority,
        identification: this.meterFrame.identification,
        transformer: this.meterFrame.transformer,
        maximumPower: this.meterFrame.maximumPower,
        limitedGridAccess: this.meterFrame.limitedGridAccess,
        maximumCurrent: this.meterFrame.maximumCurrent,
        networkPlacement: this.meterFrame.networkPlacement,
        description: this.meterFrame.description,
        capacity: this.meterFrame.capacity,
        conversionFactorA14: this.meterFrame.conversionFactorA14,
        conversionFactorA23: this.meterFrame.conversionFactorA23,
        conversionFactorR12: this.meterFrame.conversionFactorR12,
        conversionFactorR34: this.meterFrame.conversionFactorR34,
      });

      this.updateValidators(this.meterFrame.meterFrameType!);
    }
  }
}
